import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getPlugin, savePluginSettings, togglePlugin } from '../../store/AsyncMethods/PluginsMethod';
import { RESET_PLUGINS_MSG } from '../../store/Types/PluginsTypes';
import backendVerifiedUser from '../../api/backendVerifiedUser';
import { usePermissions } from '../../permissions/PermissionContext';

const MODELS = [
  { value: 'claude-opus-5-5', label: 'Claude Opus 5.5 — most capable (recommended)' },
  { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5 — faster, lower cost' },
  { value: 'claude-haiku-5-5', label: 'Claude Haiku 5.5 — fastest, lowest cost' },
];
const EFFORTS = [
  { value: 'low', label: 'Low — quick answers' },
  { value: 'medium', label: 'Medium — balanced (recommended)' },
  { value: 'high', label: 'High — more thorough, slower' },
];

const Section = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
    <h2 className="text-base font-semibold text-gray-800 border-b border-gray-100 pb-3">{title}</h2>
    {children}
  </div>
);

const selectClass =
  'w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm bg-white';

export default function ClaudePlugin() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { canOpen } = usePermissions();
  const { currentPlugin, success, error } = useSelector((s) => s.PluginsReducer);

  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('claude-opus-5-5');
  const [effort, setEffort] = useState('medium');
  const [requireConfirmation, setRequireConfirmation] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    dispatch(getPlugin('claude_ai'));
  }, []);

  const settings = currentPlugin?.plugin_key === 'claude_ai' ? currentPlugin.settings || {} : {};
  const keySaved = !!settings.api_key_set;

  useEffect(() => {
    if (currentPlugin?.plugin_key === 'claude_ai') {
      setModel(settings.model || 'claude-opus-5-5');
      setEffort(settings.effort || 'medium');
      setRequireConfirmation(settings.require_confirmation !== false);
      setApiKey('');
    }
  }, [currentPlugin]);

  useEffect(() => {
    if (success) { toast.success(success); dispatch({ type: RESET_PLUGINS_MSG }); }
    if (error) { toast.error(error); dispatch({ type: RESET_PLUGINS_MSG }); }
  }, [success, error]);

  // Blank key = keep the saved one (the saved key is never sent to the browser)
  const save = (extra = {}) =>
    dispatch(
      savePluginSettings('claude_ai', {
        api_key: apiKey.trim(),
        model,
        effort,
        require_confirmation: requireConfirmation,
        ...extra,
      })
    );

  const handleSave = async () => {
    if (!keySaved && !apiKey.trim()) {
      toast.error('Enter your Claude API key');
      return;
    }
    setSaving(true);
    await save();
    setSaving(false);
    setTestResult(null);
  };

  const handleRemoveKey = async () => {
    if (!window.confirm('Remove the saved API key? The AI Assistant will stop working until a new key is saved.')) return;
    setSaving(true);
    await save({ api_key: '', remove_api_key: true });
    if (currentPlugin?.is_active) await dispatch(togglePlugin('claude_ai'));
    setSaving(false);
  };

  const handleToggle = async () => {
    if (!currentPlugin?.is_active) {
      if (!keySaved && !apiKey.trim()) {
        toast.error('Please enter your Claude API key before activating');
        return;
      }
      const ok = await save();
      if (!ok) return;
    }
    setToggling(true);
    await dispatch(togglePlugin('claude_ai'));
    setToggling(false);
  };

  const handleTest = async () => {
    if (apiKey.trim()) {
      const ok = await save();
      if (!ok) return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await backendVerifiedUser.post('/plugins/claude/test');
      setTestResult({ ok: true, msg: res.data.success?.msg });
    } catch (err) {
      setTestResult({ ok: false, msg: err.response?.data?.error?.msg || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  const isActive = currentPlugin?.is_active;

  return (
    <div className="mx-3 pb-8 max-w-2xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/plugins')} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 rounded-xl bg-orange-500 flex items-center justify-center text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83L3 20l1.4-3.72A7.6 7.6 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Claude AI Assistant</h1>
            <p className="text-sm text-gray-500">Chat with Claude to look up and manage your store from the admin panel</p>
          </div>
        </div>
        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className="space-y-5">
        <Section title="Connection">
          <div>
            <label htmlFor="claude_api_key" className="block text-sm font-medium text-gray-700 mb-1">
              Claude API Key {!keySaved && <span className="text-rose-500">*</span>}
            </label>
            <input
              id="claude_api_key"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={keySaved ? `Saved key ending in …${settings.api_key_hint} — leave blank to keep it` : 'sk-ant-...'}
              className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent shadow-sm"
            />
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-gray-400">Stored on the server only. It is never shown again after saving.</p>
              {keySaved && (
                <button onClick={handleRemoveKey} className="text-xs text-rose-600 hover:underline">
                  Remove key
                </button>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="claude_model" className="block text-sm font-medium text-gray-700 mb-1">Model</label>
            <select id="claude_model" value={model} onChange={(e) => setModel(e.target.value)} className={selectClass}>
              {MODELS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="claude_effort" className="block text-sm font-medium text-gray-700 mb-1">Thinking effort</label>
            <select id="claude_effort" value={effort} onChange={(e) => setEffort(e.target.value)} className={selectClass}>
              {EFFORTS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-400">Higher effort gives more careful answers but takes longer and costs more.</p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={requireConfirmation}
              onChange={(e) => setRequireConfirmation(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-orange-600"
            />
            <span>
              <span className="block text-sm font-medium text-gray-700">Ask before making changes (recommended)</span>
              <span className="block text-xs text-gray-400">
                Changing order status, creating coupons, adjusting stock etc. waits for an Approve / Reject click in the chat.
              </span>
            </span>
          </label>

          <div className="flex gap-3 pt-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button
              onClick={handleToggle}
              disabled={toggling}
              className={`flex-1 py-2.5 text-sm font-medium rounded-xl transition-colors disabled:opacity-50 border ${isActive ? 'border-rose-200 text-rose-600 hover:bg-rose-50' : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'}`}
            >
              {toggling ? '...' : isActive ? 'Deactivate' : 'Activate'}
            </button>
          </div>
        </Section>

        <Section title="Test & Use">
          <div className="flex gap-3">
            <button
              onClick={handleTest}
              disabled={testing || (!keySaved && !apiKey.trim())}
              className="flex-1 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {testing ? 'Checking...' : 'Test Connection'}
            </button>
            {canOpen('/ai-assistant') && (
              <button
                onClick={() => navigate('/ai-assistant')}
                disabled={!isActive}
                title={!isActive ? 'Activate the plugin first' : ''}
                className="flex-1 py-2.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50"
              >
                Open AI Assistant
              </button>
            )}
          </div>
          {testResult && (
            <div className={`p-3 rounded-xl text-sm font-medium ${testResult.ok ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {testResult.msg}
            </div>
          )}
        </Section>

        <Section title="Setup Guide">
          <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
            <li>Sign in at <strong>platform.claude.com</strong> and add billing credits to your account</li>
            <li>Go to <strong>API Keys</strong> and create a new key, then paste it above</li>
            <li>Click <strong>Save Settings</strong>, then <strong>Test Connection</strong></li>
            <li>Click <strong>Activate</strong>, then open <strong>AI Assistant</strong> from the sidebar</li>
            <li>
              Staff can use the assistant only if their role has <strong>AI Assistant</strong> permission (Roles page). The assistant
              can only do what that admin's role allows.
            </li>
          </ol>
        </Section>
      </div>
    </div>
  );
}
